import { Flex } from '@mantine/core';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import type { DisplayCreative } from '@feathr/blackbox';
import { isBannersnackCreative } from '@feathr/blackbox';
import { Button, FullScreenModal, Spinner } from '@feathr/components';

import BannersnackEditor from '../../BannersnackEditor';

import * as styles from './BannersnackEditorModal.css';

export interface IBannersnackEditorModalProps {
  /** The creative to edit. When undefined, a centered spinner is shown and buttons are disabled. */
  creative?: DisplayCreative;
  opened: boolean;
  onDone: () => void;
  onCancel: () => void;
  /** Whether the primary action button is disabled. */
  doneDisabled?: boolean;
  /** Label for the primary button. Defaults to "Next". */
  doneLabel?: string;
  /** Optional left action (e.g., Back button) */
  leftActions?: JSX.Element;
}

function BannersnackEditorModal({
  creative,
  opened,
  onDone,
  onCancel,
  doneDisabled,
  doneLabel,
  leftActions,
}: Readonly<IBannersnackEditorModalProps>): JSX.Element {
  const { t } = useTranslation();

  const isBannersnack = creative && isBannersnackCreative(creative);
  // BannersnackEditor marks banner_hash dirty when the editor loads, then
  // clean after the user saves. A clean banner_hash means the user has
  // saved their current work — works for both new and existing creatives.
  const hasSavedInBannersnack = isBannersnack && !creative.isAttributeDirty('banner_hash');

  return (
    <FullScreenModal
      closeOnEscape={false}
      hideHeader={true}
      leftActions={leftActions}
      onClose={onCancel}
      opened={opened}
      rightActions={
        <>
          <Button name={'cancel'} onClick={onCancel}>
            {t('Cancel')}
          </Button>
          <Button
            disabled={doneDisabled || !hasSavedInBannersnack}
            name={'done'}
            onClick={onDone}
            tooltip={
              !hasSavedInBannersnack ? t('Save your creative in Bannersnack first') : undefined
            }
            type={'primary'}
          >
            {doneLabel ?? t('Next')}
          </Button>
        </>
      }
      theme={'flush'}
      title={t('Bannersnack editor')}
    >
      {isBannersnack ? (
        <BannersnackEditor
          className={styles.editorIframe}
          model={creative}
          wrapperClassName={styles.editorWrapper}
        />
      ) : (
        <Flex align={'center'} h={'100%'} justify={'center'}>
          <Spinner />
        </Flex>
      )}
    </FullScreenModal>
  );
}

export default observer(BannersnackEditorModal);
